'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { signOut } from 'next-auth/react'
import { useAuth } from '@/hooks/use-auth'
import { Menu, X } from 'lucide-react'
import { Logo } from '@/components/layout/logo'

export function Navbar() {
  const { isAuthenticated, isLoading } = useAuth()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-md shadow-sm dark:bg-encore-dark/80'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {(['#features', '#pricing', '#about'] as const).map((href) => (
              <Link
                key={href}
                href={href}
                className={`text-sm font-medium transition-colors ${
                  isScrolled
                    ? 'text-encore-gray-700 hover:text-encore-primary'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {href.slice(1).charAt(0).toUpperCase() + href.slice(2)}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {!isLoading && (
              <>
                {isAuthenticated ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => signOut({ callbackUrl: '/' })}
                      className={isScrolled ? '' : 'border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white'}
                    >
                      Logout
                    </Button>
                    <Button asChild className="bg-encore-gradient hover:opacity-90 transition-opacity">
                      <Link href="/dashboard">Go to Dashboard</Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      asChild
                      className={isScrolled ? '' : 'text-white/90 hover:text-white hover:bg-white/10'}
                    >
                      <Link href="/login">Log In</Link>
                    </Button>
                    <Button asChild className="bg-encore-gradient hover:opacity-90 transition-opacity">
                      <Link href="/register">Get Started</Link>
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className={`md:hidden p-2 transition-colors ${isScrolled ? 'text-encore-gray-700' : 'text-white/90'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md dark:bg-encore-dark/95 border-b border-gray-200 dark:border-gray-800">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
            <Link 
              href="#features" 
              className="text-base font-medium py-2 border-b border-gray-100 dark:border-gray-800"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Features
            </Link>
            <Link 
              href="#pricing" 
              className="text-base font-medium py-2 border-b border-gray-100 dark:border-gray-800"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link 
              href="#about" 
              className="text-base font-medium py-2 border-b border-gray-100 dark:border-gray-800"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About
            </Link>
            
            <div className="flex flex-col gap-3 mt-2">
              {isAuthenticated ? (
                <>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      signOut({ callbackUrl: '/' })
                    }}
                  >
                    Logout
                  </Button>
                  <Button asChild className="w-full bg-encore-gradient">
                    <Link href="/dashboard">Go to Dashboard</Link>
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" asChild className="w-full">
                    <Link href="/login">Log In</Link>
                  </Button>
                  <Button asChild className="w-full bg-encore-gradient">
                    <Link href="/register">Get Started</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

